import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './BrandingSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptyBranding = {
  logoUrl: '',
};

function BrandingSettings() {
  const [branding, setBranding] = useState(emptyBranding);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/branding`);
        const data = response.data || {};
        const logoUrl = data.logoUrl || '';
        setBranding({ logoUrl });

        if (logoUrl) {
          const isAbsolute =
            logoUrl.startsWith('http://') || logoUrl.startsWith('https://');
          const resolved = isAbsolute
            ? logoUrl
            : `${API_BASE_URL}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
          setPreviewUrl(resolved);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranding();
  }, []);

  const handleLogoUrlChange = (value) => {
    setBranding((prev) => ({ ...prev, logoUrl: value }));

    if (!value) {
      setPreviewUrl('');
      return;
    }

    const isAbsolute = value.startsWith('http://') || value.startsWith('https://');
    const resolved = isAbsolute
      ? value
      : `${API_BASE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
    setPreviewUrl(resolved);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(
        `${API_BASE_URL}/api/branding/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const uploadedPath = response.data?.url || '';
      handleLogoUrlChange(uploadedPath);
      setMessage('Logo uploaded. Remember to save changes.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      await axios.put(`${API_BASE_URL}/api/branding`, branding);
      window.dispatchEvent(
        new CustomEvent('brandingUpdated', {
          detail: { logoUrl: branding.logoUrl },
        })
      );
      setMessage('Branding updated successfully.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="branding-settings-loading">Loading branding…</p>;
  }

  return (
    <div className="branding-settings">
      <div className="branding-settings-header">
        <div>
          <h2 className="branding-settings-title">Branding</h2>
          <p className="branding-settings-subtitle">
            Manage your site logo used in the top navigation and mobile menu.
          </p>
        </div>
        <button
          type="button"
          className="branding-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="branding-settings-grid">
        <div className="branding-settings-field">
          <h3 className="branding-settings-section-title">Logo image</h3>
          <p className="branding-settings-help">
            Upload a logo (PNG, JPG, or WebP). Recommended height around 40–60px.
          </p>

          <label className="branding-settings-upload-label">
            <span className="branding-settings-upload-text">
              {isUploading ? 'Uploading…' : 'Choose image'}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </label>

          <label className="branding-settings-label">
            Logo URL
            <input
              type="text"
              className="branding-settings-input"
              value={branding.logoUrl}
              onChange={(e) => handleLogoUrlChange(e.target.value)}
              placeholder="/uploads/branding/logo.webp or https://example.com/logo.png"
            />
          </label>

          {previewUrl && (
            <div className="branding-settings-preview">
              <p className="branding-settings-preview-label">Preview</p>
              <div className="branding-settings-preview-box">
                <img src={previewUrl} alt="Logo preview" />
              </div>
            </div>
          )}
        </div>
      </div>

      {message && <p className="branding-settings-message">{message}</p>}
    </div>
  );
}

export default BrandingSettings;

